import { z } from 'zod';
import { uuidSchema, nullableDateSchema } from './common.schema.js';
import { PaginationQuerySchema, PaginationMetaSchema } from './pagination.schema';
import { LifestyleResponseSchema } from './milkProviderHealthRecords.schema.js';

// ---------------------------------------------------------------------------
// Enum constants
// ---------------------------------------------------------------------------

export const MILK_RECEIVABLE_SOURCE_TYPES = ['MOTHER', 'MOM', 'DONOR'] as const;
export const MILK_RECEIVABLE_USE_TYPES = ['PROCESS', 'DIRECT'] as const;
export const MILK_RECEIVABLE_STORAGE_CONDITIONS = ['FRESH', 'REFRIGERATED', 'FROZEN'] as const;
export const MILK_RECEIVABLE_INTENDED_USES = ['UNSPECIFIED', 'FORTIFIED', 'UNFORTIFIED'] as const;
// Includes SEND_HOME: set automatically on mother/donor discharge (see milkProvider.repo.ts /
// patient.repo.ts) — not a client-settable transition, but a valid status value nonetheless.
export const MILK_RECEIVABLE_STATUSES = ['RECEIVED', 'PROCESSED', 'DISCARDED', 'EXPIRED', 'SEND_HOME'] as const;
export type MilkReceivableStatus = typeof MILK_RECEIVABLE_STATUSES[number];
export const MILK_RECEIVABLE_DONOR_STATUSES = ['PENDING', 'CLEARED', 'APPROVED', 'REJECTED', 'EXPIRED', 'ON_HOLD'] as const;
export const DonorLifecycleStatus = {
  UNPROCESSED: 'UNPROCESSED',
  RAW:         'RAW',
  POOLED:      'POOLED',
  PASTEURIZED: 'PASTEURIZED',
  BATCHED:     'BATCHED',
  DEPLETED:    'DEPLETED',
  EXPIRED:     'EXPIRED',
  DISCARDED:   'DISCARDED',
} as const;
export type DonorMilkLifecycleStatus = typeof DonorLifecycleStatus[keyof typeof DonorLifecycleStatus];
export const DONOR_MILK_LIFECYCLE_STATUSES = Object.values(DonorLifecycleStatus) as [DonorMilkLifecycleStatus, ...DonorMilkLifecycleStatus[]];
export const MILK_RECEIVABLE_MILK_STATUSES = ['FRESH', 'FROZEN', 'THAWED'] as const;
export const MILK_RECEIVABLE_MILK_TYPES = ['PRE_TERM', 'TERM', 'FOREMILK', 'HINDMILK', 'SKIM', 'COLOSTRUM'] as const;

/**
 * Parses a comma-separated string (or repeated query-param array) into
 * an array of validated enum values.  Case-insensitive.
 * e.g. "fresh,frozen" → ['FRESH', 'FROZEN']
 *      ['FRESH', 'FROZEN'] → ['FRESH', 'FROZEN']
 */
function commaSeparatedEnum<T extends string>(allowed: readonly T[]) {
  return z.union([z.string(), z.array(z.string())])
    .transform((val) => {
      const items = Array.isArray(val) ? val : val.split(',');
      return items.map((s) => s.trim().toUpperCase());
    })
    .pipe(z.array(z.enum(allowed as unknown as [T, ...T[]])));
}

// ---------------------------------------------------------------------------
// Create request schema
// ---------------------------------------------------------------------------

export const CreateMilkReceivableSchema = z.object({
  hospital_id:    uuidSchema,
  receipt_number: z.string().min(1).max(100),
  source_type:    z.enum(MILK_RECEIVABLE_SOURCE_TYPES),
  use_type:       z.enum(MILK_RECEIVABLE_USE_TYPES).optional().default('PROCESS'),
  provider_id:    uuidSchema,
  received_at:    z.string().datetime({ offset: true, local: true }).optional(),
  volume_ml:      z.coerce.number().positive(),
  expiry_at:      nullableDateSchema,
  status:         z.enum(MILK_RECEIVABLE_STATUSES).optional().default('RECEIVED'),
  received_by:    uuidSchema,
  milk_status:    z.enum(MILK_RECEIVABLE_MILK_STATUSES).nullable().optional(),
  milk_type:      z.enum(MILK_RECEIVABLE_MILK_TYPES).nullable().optional(),
  location:       z.string().max(200).nullable().optional(),
  storage_location_id: uuidSchema.nullable().optional(),
});

export const CreateMilkReceivableRefinedSchema = CreateMilkReceivableSchema;

export type CreateMilkReceivable = z.infer<typeof CreateMilkReceivableSchema>;

// ---------------------------------------------------------------------------
// Update request schema  (all fields optional except hospital_id)
// ---------------------------------------------------------------------------

export const UpdateMilkReceivableSchema = z.object({
  hospital_id:    uuidSchema,
  source_type:    z.enum(MILK_RECEIVABLE_SOURCE_TYPES).optional(),
  use_type:       z.enum(MILK_RECEIVABLE_USE_TYPES).optional(),
  provider_id:    uuidSchema.optional(),
  received_at:    z.string().datetime({ offset: true, local: true }).optional(),
  volume_ml:      z.coerce.number().positive().optional(),
  expiry_at:      nullableDateSchema.optional(),
  status:         z.enum(MILK_RECEIVABLE_STATUSES).optional(),
  received_by:    uuidSchema.optional(),
  milk_status:    z.enum(MILK_RECEIVABLE_MILK_STATUSES).nullable().optional(),
  milk_type:      z.enum(MILK_RECEIVABLE_MILK_TYPES).nullable().optional(),
  location:       z.string().max(200).nullable().optional(),
  storage_location_id: uuidSchema.nullable().optional(),
});

export type UpdateMilkReceivable = z.infer<typeof UpdateMilkReceivableSchema>;

// ---------------------------------------------------------------------------
// Discard (soft-delete) request schema
// ---------------------------------------------------------------------------

export const DiscardMilkReceivableSchema = z.object({
  notes: z.string().min(1).max(1000),
});

export type DiscardMilkReceivable = z.infer<typeof DiscardMilkReceivableSchema>;

// ---------------------------------------------------------------------------
// Response schema  (Prisma Decimal + Date → string after .parse())
// ---------------------------------------------------------------------------

export const MilkReceivableResponseSchema = z.object({
  receivable_id:                uuidSchema,
  hospital_id:                  uuidSchema,
  receipt_number:               z.string(),
  source_type:                  z.string(),
  use_type:                     z.string(),
  provider_id:                  uuidSchema,
  provider_first_name:          z.string(),
  provider_last_name:           z.string(),
  provider_person_type:         z.string().nullable(),
  intended_patient_id:          uuidSchema.nullable(),
  encounter_id:                 uuidSchema.nullable(),
  collected_at:                 z.string(),
  received_at:                  z.string(),
  volume_ml:                    z.string(),
  /** Sum of milk_inventory.remaining_volume_ml for AVAILABLE items linked to this receivable. Falls back to volume_ml when no inventory exists yet. */
  remaining_volume_ml:          z.string(),
  collection_method:            z.string().nullable(),
  storage_condition_on_receipt: z.string(),
  intended_use:                 z.string(),
  expiry_at:                    z.string().nullable(),
  status:                       z.string(),
  received_by:                  uuidSchema,
  notes:                        z.string().nullable(),
  raw_authorised_by:            uuidSchema.nullable(),
  raw_authorisation_reason:     z.string().nullable(),
  milk_status:                  z.string().nullable(),
  milk_type:                    z.string().nullable(),
  location:                     z.string().nullable(),
  storage_location_id:          uuidSchema.nullable().default(null),
  donor_status:                  z.string(),
  created_at:                   z.string(),
  updated_at:                   z.string(),
  batch_id:                     uuidSchema.nullable(),
  pool_id:                      uuidSchema.nullable().optional(),
  pool_number:                  z.string().nullable().optional(),
  batch_number:                 z.string().nullable().optional(),
  /** Every non-deleted batch created from the receivable's pool, oldest first. Donor inventory only. */
  batches:                      z.array(z.object({ batch_id: uuidSchema, batch_number: z.string() })).optional(),
  lifestyle:                    LifestyleResponseSchema.nullable(),
  computed_status:              z.enum(DONOR_MILK_LIFECYCLE_STATUSES).optional(),
});

export type MilkReceivableResponse = z.infer<typeof MilkReceivableResponseSchema>;

// ---------------------------------------------------------------------------
// List query schema
// ---------------------------------------------------------------------------

export const ListMilkReceivablesQuerySchema = PaginationQuerySchema.omit({ sortBy: true, sortOrder: true }).extend({
  source_type:  z.enum(MILK_RECEIVABLE_SOURCE_TYPES).optional(),
  status:       commaSeparatedEnum(MILK_RECEIVABLE_STATUSES).optional(),
  milk_status:  commaSeparatedEnum(MILK_RECEIVABLE_MILK_STATUSES).optional(),
  milk_type:    commaSeparatedEnum(MILK_RECEIVABLE_MILK_TYPES).optional(),
  donor_status: commaSeparatedEnum(MILK_RECEIVABLE_DONOR_STATUSES).optional(),
  provider_id:  uuidSchema.optional(),
  search:       z.string().trim().min(1).max(100).optional().describe('Search by container ID (receipt_number) or provider name'),
});
export type ListMilkReceivablesQuery = z.infer<typeof ListMilkReceivablesQuerySchema>;

export const ListMilkReceivablesResponseSchema = z.object({
  items:      z.array(MilkReceivableResponseSchema),
  pagination: PaginationMetaSchema,
});
export type ListMilkReceivablesResponse = z.infer<typeof ListMilkReceivablesResponseSchema>;

// ---------------------------------------------------------------------------
// Donor inventory summary  (GET /milk-receivables/donor-inventory/summary)
// One entry per lifecycle status, always all of them, zeros included.
// volume_ml is the total received volume of the bottles currently in that status.
// ---------------------------------------------------------------------------

export const DonorInventorySummaryQuerySchema = z.object({
  hospital_id: uuidSchema,
  provider_id: uuidSchema,
});
export type DonorInventorySummaryQuery = z.infer<typeof DonorInventorySummaryQuerySchema>;

export const DonorInventorySummaryItemSchema = z.object({
  status:       z.enum(DONOR_MILK_LIFECYCLE_STATUSES),
  bottle_count: z.number().int().nonnegative(),
  volume_ml:    z.number().nonnegative(),
});

export const DonorInventorySummaryResponseSchema = z.object({
  items: z.array(DonorInventorySummaryItemSchema),
});
export type DonorInventorySummaryResponse = z.infer<typeof DonorInventorySummaryResponseSchema>;

// ---------------------------------------------------------------------------
// Split request schema
// Body = all CreateMilkReceivableSchema fields (for the new DONOR receivable)
//      + original_receivable_id  — the MOTHER receivable being split
//      + original_volume_ml      — volume to retain on the original MOTHER record (0 = whole container)
// ---------------------------------------------------------------------------

export const SplitMilkReceivableSchema = CreateMilkReceivableSchema.extend({
  original_receivable_id: uuidSchema,
  // z.coerce turns null, '', booleans, arrays and '0x0' into numbers (often 0), which would convert the whole container by accident; only real numbers and plain decimal strings pass.
  original_volume_ml:     z.preprocess((v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
    if (typeof v === 'string' && /^\s*\d+(\.\d+)?\s*$/.test(v)) return Number(v.trim());
    return NaN;
  }, z.coerce.number().nonnegative()),
});
export type SplitMilkReceivable = z.infer<typeof SplitMilkReceivableSchema>;

export const SplitMilkReceivableResponseSchema = z.object({
  mother: MilkReceivableResponseSchema,
  donor:  MilkReceivableResponseSchema,
});
export type SplitMilkReceivableResponse = z.infer<typeof SplitMilkReceivableResponseSchema>;

// ---------------------------------------------------------------------------
// Bulk convert MOTHER → DONOR schema
// Body = array of { receivable_id } objects.
// Each bottle's own provider is validated in-place (person_type=BOTH,
// screening_status=APPROVED) and source_type is flipped to DONOR.
// Same receivable_id, volume, and provider — only source_type changes.
// ---------------------------------------------------------------------------

export const BulkConvertMotherToDonorItemSchema = z.object({
  receivable_id: uuidSchema,
});
export type BulkConvertMotherToDonorItem = z.infer<typeof BulkConvertMotherToDonorItemSchema>;

export const BulkConvertMotherToDonorFailedItemSchema = z.object({
  receivable_id: uuidSchema,
  reason:        z.string(),
});

export const BulkConvertMotherToDonorResponseSchema = z.object({
  converted: z.array(MilkReceivableResponseSchema),
  failed:    z.array(BulkConvertMotherToDonorFailedItemSchema),
});
export type BulkConvertMotherToDonorResponse = z.infer<typeof BulkConvertMotherToDonorResponseSchema>;

// ---------------------------------------------------------------------------
// Bulk create response schema
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Convert bottle validation query schema
// Used by GET /milk-receivables/convert-bottle-validation
// ---------------------------------------------------------------------------

export const ConvertBottleValidationQuerySchema = z.object({
  receipt_number: z.string().min(1),
});
export type ConvertBottleValidationQuery = z.infer<typeof ConvertBottleValidationQuerySchema>;

// ---------------------------------------------------------------------------
// Bulk create response schema
// ---------------------------------------------------------------------------

export const BulkCreateFailedItemSchema = z.object({
  receipt_number: z.string(),
  reason:         z.string(),
});

export const BulkCreateMilkReceivablesResponseSchema = z.object({
  created: z.array(MilkReceivableResponseSchema),
  failed:  z.array(BulkCreateFailedItemSchema),
});

export type BulkCreateMilkReceivablesResponse = z.infer<typeof BulkCreateMilkReceivablesResponseSchema>;
